/**
 * Auto-manages a delimited "Backorder requests" section inside the cart
 * note so the order's special instructions stay in sync with current
 * backorder line items.
 *
 * Note structure becomes:
 *   {customer's own note if any}
 *
 *   --- Backorder requests ---
 *   [Backorder] {Product Title} — Size requested: {size}{ × N if >1}
 *   [Backorder] ...
 *   --- End backorder requests ---
 *
 * If there are no backorder items, the markers (and any contents between
 * them) are removed entirely. The customer's own note above the markers
 * is preserved.
 */
(function () {
  const START_MARKER = '--- DO NOT DELETE ---';
  const END_MARKER = '--- DO NOT DELETE ---';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function buildBackorderBlock(items) {
    const lines = items
      .filter((it) => it.properties && it.properties.Backorder === 'Yes')
      .map((it) => {
        const size = (it.properties['Size requested'] || '').toString().trim() || '(not specified)';
        const qty = it.quantity > 1 ? ` × ${it.quantity}` : '';
        return `[Backorder] ${it.product_title} — Size requested: ${size}${qty}`;
      });
    if (lines.length === 0) return '';
    return [START_MARKER, ...lines, END_MARKER].join('\n');
  }

  function stripExistingBlock(note) {
    if (!note) return '';
    // Greedy match anything between the markers, including the markers themselves.
    const pattern = new RegExp(
      `\\n*${escapeRe(START_MARKER)}[\\s\\S]*?${escapeRe(END_MARKER)}\\n*`,
      'g'
    );
    return note.replace(pattern, '\n').replace(/\n{3,}/g, '\n\n').trim();
  }

  function escapeRe(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function buildNewNote(currentNote, backorderBlock) {
    const stripped = stripExistingBlock(currentNote);
    if (!backorderBlock) return stripped;
    return stripped ? `${stripped}\n\n${backorderBlock}` : backorderBlock;
  }

  let inFlight = false;
  let lastWrittenNote = null;

  async function syncNote() {
    if (inFlight) return;
    inFlight = true;
    try {
      const cartRes = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartRes.ok) return;
      const cart = await cartRes.json();

      const block = buildBackorderBlock(cart.items || []);
      const desired = buildNewNote(cart.note || '', block);
      const current = (cart.note || '').toString();

      // No-op: nothing to change.
      if (desired === current.trim()) return;
      // Avoid feedback loop if our own write triggered the event.
      if (desired === lastWrittenNote) return;

      lastWrittenNote = desired;
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: desired }),
      });

      // The cart page / drawer have already re-rendered with the *old* note
      // (we wrote the new one after their fetch). Push the fresh value into
      // any visible <textarea name="note"> so the customer sees it without a
      // hard refresh.
      reflectNoteInTextareas(desired);
    } catch (e) {
      // Silent — note sync is a nice-to-have; cart still works.
    } finally {
      inFlight = false;
    }
  }

  function reflectNoteInTextareas(note) {
    const textareas = document.querySelectorAll('textarea[name="note"]');
    textareas.forEach((ta) => {
      // Don't clobber what the customer is actively typing.
      if (document.activeElement === ta) return;
      if (ta.value === note) return;
      ta.value = note;
      // Fire input so any cart-note custom element listeners stay in sync.
      ta.dispatchEvent(new Event('input', { bubbles: true }));
      ta.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function attach() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        // Defer slightly so any same-tick writes settle.
        setTimeout(syncNote, 50);
      });
    }
    // Also catch our own custom events from request-size + cart-hover.
    document.addEventListener('cart-update', () => setTimeout(syncNote, 50));
    document.addEventListener('cart:refresh', () => setTimeout(syncNote, 50));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach, { once: true });
  } else {
    attach();
  }
})();
